import { useState, useEffect } from "react";
import { Icon, api } from "../lib/core";

export const NotificationsDropdown = ({ onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await api.get("/notifications");
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (err) {
      console.error("Failed to mark as read:", err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      reminder: "notifications",
      auto_success: "check_circle",
      auto_failed: "error",
      insufficient_balance: "account_balance_wallet"
    };
    return icons[type] || "info";
  };

  const getNotificationColor = (type) => {
    const colors = {
      reminder: "text-blue-500 bg-blue-50",
      auto_success: "text-green-500 bg-green-50",
      auto_failed: "text-red-500 bg-red-50",
      insufficient_balance: "text-amber-500 bg-amber-50"
    };
    return colors[type] || "text-slate-500 bg-slate-50";
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose}>
      <div 
        className="absolute top-16 right-4 w-80 max-h-[70vh] bg-white rounded-2xl shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-slate-900">Notifications</h3>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="text-sm text-primary-custom hover:underline"
            >
              Mark all read
            </button>
          )}
        </div>
        
        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center">
              <Icon name="progress_activity" className="text-primary-custom text-3xl animate-spin" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              <Icon name="notifications_off" className="text-4xl" />
              <p className="mt-2">No notifications</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-4 border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${
                  !notification.is_read ? "bg-primary-custom/5" : ""
                }`}
                onClick={() => handleMarkRead(notification.id)}
              >
                <div className="flex gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getNotificationColor(notification.type)}`}>
                    <Icon name={getNotificationIcon(notification.type)} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium text-slate-900 ${!notification.is_read ? "font-semibold" : ""}`}>
                      {notification.title}
                    </p>
                    <p className="text-sm text-slate-500 line-clamp-2">{notification.message}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(notification.created_at).toLocaleString()}
                    </p>
                  </div>
                  {!notification.is_read && (
                    <div className="w-2 h-2 bg-primary-custom rounded-full mt-2" />
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// ANALYTICS PAGE — Overview + Breakdown tabs
// ============================================================

